import React from "react";

function WonderLandingView({
  renderWonderDisplayText,
  t,
  worldOrder,
  worldMeta,
  getWorldDisplayName,
  setPage
}) {
  return (
    <div className="aboutPage wonderLandingPage">
      <section className="homeGamePanel wonderLandingPanel" aria-labelledby="wonder-landing-title">
        <div className="homeGameHeader wonderLandingHeader">
          {renderWonderDisplayText(t("game_title"), {
            as: "span",
            className: "homeGameBadge wonderGlyphHeading wonderGlyphHeading--badge",
            variant: "small"
          })}
          {renderWonderDisplayText(t("home_welcome_title"), {
            as: "h2",
            className: "homeGameTitle wonderLandingTitle wonderGlyphHeading wonderGlyphHeading--hero",
            variant: "hero"
          })}
          <p className="homeHeroSubtitle wonderLandingSubtitle">{t("home_welcome_subtitle")}</p>
          <p className="wonderLandingLead">{t("home_hero_subtitle")}</p>
        </div>

        <div className="homeWorldPreviewRow wonderLandingWorldRow" aria-label="Wonder World highlights">
          {worldOrder.map((worldId) => (
            <div className="homeWorldPreviewItem wonderLandingWorldItem" key={`wonder-landing-${worldId}`}>
              <span className="homePreviewIcon wonderLandingPreviewIcon">{worldMeta[worldId]?.icon}</span>
              <span className="homePreviewLabel">{getWorldDisplayName(worldId)}</span>
            </div>
          ))}
        </div>

        <div className="homePrimaryActions wonderLandingActions">
          <button className="primaryButton" onClick={() => setPage("kid")}>
            {renderWonderDisplayText(t("start_adventure"), {
              as: "span",
              className: "wonderGlyphHeading wonderGlyphHeading--button",
              variant: "small"
            })}
          </button>
          <button className="homeSecondaryButton" onClick={() => setPage("about")}>
            {renderWonderDisplayText(t("for_parents"), {
              as: "span",
              className: "wonderGlyphHeading wonderGlyphHeading--button",
              variant: "small"
            })}
          </button>
        </div>

        <div className="homeGameArtPreview wonderLandingPreview" aria-hidden="true">
          <div className="homeGameArtPreviewImage" />
        </div>
      </section>
    </div>
  );
}

function WonderMapView({
  renderWonderDisplayText,
  t,
  worldOrder,
  worldMeta,
  chapterIntroCopy,
  worldCardCopy,
  world,
  completed,
  unlockedWorlds,
  startWorld,
  getWorldDisplayName,
  localizeText,
  stars
}) {
  return (
    <div className="sectionPanel mapSectionPanel">
      <div className="mapWrapper">
        {renderWonderDisplayText(t("adventure_map"), {
          as: "h2",
          className: "wonderGlyphHeading wonderGlyphHeading--section",
          variant: "section"
        })}

        <div className="mapPath">
          {worldOrder.map((w, i) => {
            const unlocked = unlockedWorlds.includes(w);
            const meta = worldMeta[w];
            const introCopy = chapterIntroCopy[w];
            const cardCopy = worldCardCopy[w];
            const isCurrentWorld = w === world && !completed.includes(w);

            return (
              <React.Fragment key={w}>
                <div className="mapStop">
                  <div
                    className={`mapNode ${completed.includes(w) ? "done" : ""} ${
                      !unlocked ? "locked" : completed.includes(w) ? "" : "pulse"
                    } ${isCurrentWorld ? "currentQuest" : ""}`}
                    onClick={() => unlocked && startWorld(w)}
                    title={localizeText(introCopy?.mapTag || "")}
                  >
                    {meta.icon}
                  </div>
                  <div className="mapTextPlate">
                    {renderWonderDisplayText(
                      localizeText(cardCopy?.title || introCopy?.mapTitle || meta.name?.en || w),
                      {
                        as: "div",
                        className: "mapWorldName wonderGlyphHeading wonderGlyphHeading--world",
                        variant: "card"
                      }
                    )}
                    <p className="mapWorldBlurb">
                      {localizeText(cardCopy?.blurb || introCopy?.mapSubtitle || "")}
                    </p>
                    <p className="mapWorldTag">{localizeText(introCopy?.mapTag || "")}</p>
                    <p className={`mapWorldState ${unlocked ? "open" : "locked"}`}>
                      {unlocked ? t("unlocked_world") : t("locked_world")}
                    </p>
                  </div>
                </div>
                {i < worldOrder.length - 1 && (
                  <div className={`mapArrow ${i === 2 ? "mapArrowTurn" : ""}`} aria-hidden="true">
                    ——&gt;
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>

        <p className="progress">
          {t("progress", { done: completed.length, total: worldOrder.length })}
        </p>

        <div className="starBar">{t("stars", { stars })}</div>
      </div>
    </div>
  );
}

function WonderStoryView({
  activeNode,
  renderWonderDisplayText,
  t,
  getWorldDisplayName,
  world,
  levelState,
  localizeText,
  usedQuestionChoiceKeys,
  getQuestionChoiceKey,
  node,
  choose,
  goToMap
}) {
  return (
    <div className="card storyScreen">
      {activeNode ? (
        <>
          {renderWonderDisplayText(t("chapter", { world: getWorldDisplayName(world), level: levelState }), {
            as: "h2",
            className: "wonderGlyphHeading wonderGlyphHeading--story",
            variant: "section"
          })}

          <p className="storyFlowText">{localizeText(activeNode.story)}</p>

          {activeNode.choices
            .filter((choice) => {
              if (choice.next !== "question") return true;
              return !usedQuestionChoiceKeys.includes(
                getQuestionChoiceKey(world, node, choice.text)
              );
            })
            .map((choice, index) => (
              <button className="storyFlowButton" key={index} onClick={() => choose(choice)}>
                {localizeText(choice.text)}
              </button>
            ))}
        </>
      ) : (
        <>
          {renderWonderDisplayText(t("progress_restored"), {
            as: "h2",
            className: "wonderGlyphHeading wonderGlyphHeading--story",
            variant: "section"
          })}
          <p className="storyFlowText">{t("story_restore_hint")}</p>
        </>
      )}

      <div className="returnRow">
        <button className="backMapButton returnButton" onClick={goToMap}>
          {renderWonderDisplayText(t("back_to_map"), {
            as: "span",
            className: "wonderGlyphHeading wonderGlyphHeading--button",
            variant: "small"
          })}
        </button>
      </div>
    </div>
  );
}

function WonderQuestionView({
  question,
  lastQuestionType,
  activeBossCopy,
  activeBossMiniCopy,
  renderWonderDisplayText,
  localizeText,
  t,
  answer,
  goToMap
}) {
  return (
    <div className="card storyScreen">
      {question ? (
        <>
          {lastQuestionType === "boss" && (activeBossCopy || activeBossMiniCopy) && (
            <div className="chapterIntroBlock bossIntroBlock">
              {renderWonderDisplayText(localizeText(activeBossCopy?.title || activeBossMiniCopy?.title || ""), {
                as: "h3",
                className: "chapterIntroTitle wonderGlyphHeading wonderGlyphHeading--minor",
                variant: "card"
              })}
              {activeBossCopy?.challengeLine && (
                <p className="storyFlowText">{localizeText(activeBossCopy.challengeLine)}</p>
              )}
              <p className="storyFlowText bossObjectiveLine">
                {localizeText(activeBossCopy?.objective || activeBossMiniCopy?.objective || "")}
              </p>
            </div>
          )}
          <p className="context storyFlowText">{localizeText(question.context)}</p>
          <p className="question storyFlowText">{localizeText(question.q)}</p>

          {question.options.map((option) => (
            <button className="storyFlowButton" key={option} onClick={() => answer(option)}>
              {localizeText(option)}
            </button>
          ))}
        </>
      ) : (
        <>
          {renderWonderDisplayText(t("question_restored"), {
            as: "h2",
            className: "wonderGlyphHeading wonderGlyphHeading--story",
            variant: "section"
          })}
          <p className="storyFlowText">{t("question_restore_hint")}</p>
          <div className="returnRow">
            <button className="backMapButton returnButton" onClick={goToMap}>
              {renderWonderDisplayText(t("back_to_map"), {
                as: "span",
                className: "wonderGlyphHeading wonderGlyphHeading--button",
                variant: "small"
              })}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function WonderResultView({
  lastResultKind,
  activeBossCopy,
  activeChapterCopy,
  activeBossMiniCopy,
  renderWonderDisplayText,
  localizeText,
  t,
  lastAnswerCorrect,
  finishLevel
}) {
  return (
    <div className="card storyScreen">
      {lastResultKind === "boss" && (activeBossCopy || activeChapterCopy) ? (
        <>
          {renderWonderDisplayText(localizeText(activeBossCopy?.successTitle || activeChapterCopy?.completionTitle || ""), {
            as: "h2",
            className: "wonderGlyphHeading wonderGlyphHeading--story",
            variant: "section"
          })}
          <p className="storyFlowText">
            {localizeText(activeBossCopy?.successBody || activeChapterCopy?.completionBody || "")}
          </p>
          {activeChapterCopy?.lumiLine && (
            <p className="storyFlowText"><em>{localizeText(activeChapterCopy.lumiLine)}</em></p>
          )}
          <p className="storyFlowText">
            {localizeText(activeBossCopy?.victoryHint || activeChapterCopy?.nextHint || "")}
          </p>
          {activeBossMiniCopy?.success && (
            <p className="storyFlowText bossMiniSuccess">{localizeText(activeBossMiniCopy.success)}</p>
          )}
        </>
      ) : (
        <>
          {renderWonderDisplayText(lastAnswerCorrect ? t("nice_work") : t("keep_going"), {
            as: "h2",
            className: "wonderGlyphHeading wonderGlyphHeading--story",
            variant: "section"
          })}
          <p className="storyFlowText">
            {lastAnswerCorrect ? t("answer_helped") : t("learned_continue")}
          </p>
        </>
      )}

      <button className="storyFlowButton" onClick={finishLevel}>
        {renderWonderDisplayText(t("continue"), {
          as: "span",
          className: "wonderGlyphHeading wonderGlyphHeading--button",
          variant: "small"
        })}
      </button>
    </div>
  );
}

function WonderSummaryView({
  renderWonderDisplayText,
  showFinalEndingAfterSummary,
  activeChapterCopy,
  t,
  localizeText,
  getWorldDisplayName,
  world,
  lang,
  setShowFinalEndingAfterSummary,
  setPage,
  goToMap
}) {
  return (
    <div className="card storyScreen">
      {renderWonderDisplayText(
        showFinalEndingAfterSummary
          ? (activeChapterCopy ? localizeText(activeChapterCopy.summaryTitle) : t("adventure_complete"))
          : t("adventure_complete"),
        {
          as: "h2",
          className: "wonderGlyphHeading wonderGlyphHeading--story",
          variant: "section"
        }
      )}
      <p className="storyFlowText">
        {showFinalEndingAfterSummary
          ? (activeChapterCopy
            ? localizeText(activeChapterCopy.summaryBody)
            : t("world_complete", { world: getWorldDisplayName(world) }))
          : t("world_passed_message")}
      </p>

      <div className="returnRow">
        <button
          className="backMapButton returnButton"
          onClick={() => {
            if (showFinalEndingAfterSummary) {
              setShowFinalEndingAfterSummary(false);
              setPage("finalEnding");
              return;
            }
            goToMap();
          }}
        >
          {showFinalEndingAfterSummary
            ? renderWonderDisplayText(lang === "zh" ? "最终结局" : localizeText("Final Ending"), {
                as: "span",
                className: "wonderGlyphHeading wonderGlyphHeading--button",
                variant: "small"
              })
            : renderWonderDisplayText(t("back_to_map"), {
                as: "span",
                className: "wonderGlyphHeading wonderGlyphHeading--button",
                variant: "small"
              })}
        </button>
      </div>
    </div>
  );
}

function WonderFinalEndingView({
  renderWonderDisplayText,
  localizeText,
  chapterCopy,
  t,
  goToMap
}) {
  return (
    <div className="card storyScreen">
      {renderWonderDisplayText(localizeText(chapterCopy.finalEnding.title), {
        as: "h2",
        className: "wonderGlyphHeading wonderGlyphHeading--story",
        variant: "section"
      })}
      <p className="storyFlowText">{localizeText(chapterCopy.finalEnding.body)}</p>
      <p className="storyFlowText"><em>{localizeText(chapterCopy.finalEnding.lumiLine)}</em></p>
      <p className="storyFlowText">{localizeText(chapterCopy.finalEnding.closingTag)}</p>

      <div className="returnRow">
        <button className="returnButton" onClick={goToMap}>
          {renderWonderDisplayText(t("back_to_map"), {
            as: "span",
            className: "wonderGlyphHeading wonderGlyphHeading--button",
            variant: "small"
          })}
        </button>
      </div>
    </div>
  );
}

function WonderFeedbackView({
  renderWonderDisplayText,
  t,
  feedbackChoice,
  setFeedbackChoice,
  feedbackNote,
  setFeedbackNote,
  submitHomeFeedback,
  goHome
}) {
  return (
    <div className="card feedbackCard">
      {renderWonderDisplayText(t("feedback_title"), {
        as: "h2",
        className: "wonderGlyphHeading wonderGlyphHeading--story",
        variant: "section"
      })}
      <p className="storyFlowText">{t("feedback_prompt")}</p>

      <div className="feedbackChoiceRow">
        <button
          className={`storyFlowButton feedbackChoiceButton ${feedbackChoice === "love" ? "selected" : ""}`}
          onClick={() => setFeedbackChoice("love")}
        >
          {t("loved_it")}
        </button>
        <button
          className={`storyFlowButton feedbackChoiceButton ${feedbackChoice === "ok" ? "selected" : ""}`}
          onClick={() => setFeedbackChoice("ok")}
        >
          {t("it_was_ok")}
        </button>
        <button
          className={`storyFlowButton feedbackChoiceButton ${feedbackChoice === "boring" ? "selected" : ""}`}
          onClick={() => setFeedbackChoice("boring")}
        >
          {t("not_fun")}
        </button>
      </div>

      <p className="feedbackNoteLabel">{t("feedback_note_label")}</p>
      <textarea
        className="feedbackInput"
        value={feedbackNote}
        onChange={(event) => setFeedbackNote(event.target.value)}
        placeholder={t("feedback_note_placeholder")}
        rows={5}
      />

      <button className="storyFlowButton" onClick={submitHomeFeedback}>
        {renderWonderDisplayText(t("feedback_send"), {
          as: "span",
          className: "wonderGlyphHeading wonderGlyphHeading--button",
          variant: "small"
        })}
      </button>

      <div className="returnRow">
        <button className="returnButton" onClick={goHome}>
          {renderWonderDisplayText(t("back_home"), {
            as: "span",
            className: "wonderGlyphHeading wonderGlyphHeading--button",
            variant: "small"
          })}
        </button>
      </div>
    </div>
  );
}

function WonderParentView({
  renderWonderDisplayText,
  t,
  history,
  getCognitiveType,
  getCognitiveTypeLabel,
  lang,
  generateSummary,
  pickInline,
  generateTrendReport,
  getWorldDisplayName,
  sessionStart,
  setShowClearConfirm,
  goHome
}) {
  if (history.length === 0) {
    return (
      <div className="card parentCard">
        {renderWonderDisplayText(t("parent_report"), {
          as: "h2",
          className: "wonderGlyphHeading wonderGlyphHeading--story",
          variant: "section"
        })}
        <div className="parentBody">
          <p>{t("parent_no_data")}</p>
          <p>{t("parent_no_data_hint")}</p>
        </div>
      </div>
    );
  }

  const latest = history[0];
  const latestType = getCognitiveTypeLabel(getCognitiveType(latest), lang);
  const latestSummary = generateSummary(latest, { lang, pickInline });
  const trend = generateTrendReport(history, { lang, pickInline });
  const sessionMinutes = Math.max(1, Math.round((Date.now() - sessionStart) / 60000));

  return (
    <div className="card parentCard">
      {renderWonderDisplayText(t("parent_report"), {
        as: "h2",
        className: "wonderGlyphHeading wonderGlyphHeading--story",
        variant: "section"
      })}

      <div className="parentBody">
        {renderWonderDisplayText(t("latest_session"), {
          as: "h3",
          className: "wonderGlyphHeading wonderGlyphHeading--minor",
          variant: "card"
        })}
        <p><b>{t("world")}:</b> {getWorldDisplayName(latest.world)}</p>
        <p><b>{t("played")}:</b> {latest.createdAt}</p>
        <p><b>{t("session_duration")}:</b> {sessionMinutes} {t("minutes")}</p>
        <p><b>{t("questions_answered")}:</b> {latest.questionCount}</p>
        <p><b>{t("accuracy")}:</b> {latest.accuracyRate}%</p>
        <p><b>{t("stars_earned")}:</b> {latest.stars}</p>

        <hr />

        {renderWonderDisplayText(t("latest_profile"), {
          as: "h3",
          className: "wonderGlyphHeading wonderGlyphHeading--minor",
          variant: "card"
        })}
        <p><b>{t("type")}:</b> {latestType}</p>
        <p><b>{t("exploration")}:</b> {latest.exploration}</p>
        <p><b>{t("analysis")}:</b> {latest.analysis}</p>
        <p><b>{t("risk")}:</b> {latest.risk}</p>
        <p><b>{t("curiosity")}:</b> {latest.curiosity}</p>
        <p><b>{t("care_responsibility")}:</b> {latest.care}</p>
        <p><b>{t("logic_preference")}:</b> {latest.logic}</p>

        <hr />

        {renderWonderDisplayText(t("latest_narrative"), {
          as: "h3",
          className: "wonderGlyphHeading wonderGlyphHeading--minor",
          variant: "card"
        })}
        <p style={{ whiteSpace: "pre-line", lineHeight: "1.6" }}>
          {latestSummary}
        </p>

        <hr />

        {renderWonderDisplayText(t("overall_trend"), {
          as: "h3",
          className: "wonderGlyphHeading wonderGlyphHeading--minor",
          variant: "card"
        })}
        <p><b>{t("profile_type")}:</b> {getCognitiveTypeLabel(trend.type, lang)}</p>
        <p><b>{t("average_accuracy")}:</b> {trend.avgAccuracy}%</p>
        <p><b>{t("total_sessions")}:</b> {history.length}</p>
        <p><b>{t("total_stars")}:</b> {trend.totals.stars}</p>

        <p style={{ whiteSpace: "pre-line", lineHeight: "1.6" }}>
          {trend.narrative}
        </p>

        <hr />

        {renderWonderDisplayText(t("recent_sessions"), {
          as: "h3",
          className: "wonderGlyphHeading wonderGlyphHeading--minor",
          variant: "card"
        })}
        <ul style={{ textAlign: "left", maxWidth: "500px", margin: "0 auto 20px" }}>
          {history.slice(0, 5).map((item) => (
            <li key={item.id}>
              {getWorldDisplayName(item.world)} — {item.accuracyRate}% {t("accuracy_inline")} — {item.createdAt}
            </li>
          ))}
        </ul>

        <div className="parentGentleNote">
          {renderWonderDisplayText(t("gentle_note_title"), {
            as: "h3",
            className: "wonderGlyphHeading wonderGlyphHeading--minor",
            variant: "card"
          })}
          <p>{t("gentle_note_body")}</p>
        </div>

        <button onClick={() => setShowClearConfirm(true)}>
          {renderWonderDisplayText(t("clear_report_history"), {
            as: "span",
            className: "wonderGlyphHeading wonderGlyphHeading--button",
            variant: "small"
          })}
        </button>
        <div className="returnRow">
          <button className="returnButton" onClick={goHome}>
            {renderWonderDisplayText(t("back_home"), {
              as: "span",
              className: "wonderGlyphHeading wonderGlyphHeading--button",
              variant: "small"
            })}
          </button>
        </div>
      </div>
    </div>
  );
}

function WonderClearDialog({
  showClearConfirm,
  t,
  handleArchiveAndClear,
  handleClearWithoutArchive,
  closeClearDialogWithoutChanges
}) {
  if (!showClearConfirm) {
    return null;
  }

  return (
    <div className="clearOverlay">
      <div className="clearDialog">
        <h3 className="clearDialogTitle">{t("clear_confirm_title")}</h3>
        <p className="clearDialogText">{t("clear_confirm_text")}</p>
        <div className="clearDialogActions">
          <button className="clearYesButton" onClick={handleArchiveAndClear}>
            {t("clear_yes_download")}
          </button>
          <button className="clearNoButton" onClick={handleClearWithoutArchive}>
            {t("clear_no_delete_anyway")}
          </button>
          <button className="clearCancelButton" onClick={closeClearDialogWithoutChanges}>
            {t("cancel")}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function WonderWorldViews(props) {
  const {
    page,
    showClearConfirm
  } = props;

  return (
    <>
      {page === "wonderHome" ? <WonderLandingView {...props} /> : null}
      {page === "kid" ? <WonderMapView {...props} /> : null}
      {page === "story" ? <WonderStoryView {...props} /> : null}
      {page === "question" ? <WonderQuestionView {...props} /> : null}
      {page === "result" ? <WonderResultView {...props} /> : null}
      {page === "summary" ? <WonderSummaryView {...props} /> : null}
      {page === "finalEnding" ? <WonderFinalEndingView {...props} /> : null}
      {page === "feedback" ? <WonderFeedbackView {...props} /> : null}
      {page === "parent" ? <WonderParentView {...props} /> : null}
      <WonderClearDialog showClearConfirm={showClearConfirm} {...props} />
    </>
  );
}
